import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  TextField,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";

const DetectionConfigurator = ({ sensors, sensorDetections, onAdd, onClose }) => {
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [selectedDetection, setSelectedDetection] = useState(null);
  const [detectionParams, setDetectionParams] = useState({});
  const [appliedOn, setAppliedOn] = useState(""); // Store selected dataSchema property
  const [logic, setLogic] = useState("AND");

  const handleSensorSelection = (sensor) => {
    setSelectedSensor(sensor);
    setSelectedDetection(null); // Reset detection on sensor change
    setDetectionParams({});
    setAppliedOn(""); // Reset appliedOn
  };

  const handleDetectionSelection = (detection) => {
    setSelectedDetection(detection);
    setDetectionParams(
      Object.keys(detection.params_template).reduce((acc, key) => {
        acc[key] = ""; // Initialize all params with empty strings
        return acc;
      }, {})
    );
  };

  const handleParamChange = (key, value) => {
    setDetectionParams((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const isAddButtonEnabled = () => {
    if (!selectedSensor || !selectedDetection || !appliedOn) return false;
    return Object.entries(detectionParams).every(([key, value]) => value.trim() !== "");
  };

  const handleAddDetection = () => {
    const newDetection = {
      logic,
      sensorId: selectedSensor._id,
      sensorName: selectedSensor.sensor_name,
      detectionId: selectedDetection._id,
      detectionName: selectedDetection.name,
      appliedOn, // Store the selected data point from dataSchema
      params: detectionParams,
    };
    onAdd(newDetection);
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Configure Detection</DialogTitle>
      <DialogContent>
        <Box mb={3}>
          <Typography variant="h6">Step 1: Select a Sensor</Typography>
          <Grid container spacing={2} mt={2}>
            {sensors.map((sensor) => (
              <Grid item xs={4} key={sensor._id}>
                <Card
                  onClick={() => handleSensorSelection(sensor)}
                  style={{
                    border: selectedSensor?._id === sensor._id ? "2px solid green" : "none",
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle1">{sensor.sensor_name}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {selectedSensor && (
          <Box mb={3}>
            <Typography variant="h6">Step 2: Select a Detection</Typography>
            <Grid container spacing={2} mt={2}>
              {(sensorDetections[selectedSensor._id] || []).map((detection) => (
                <Grid item xs={4} key={detection._id}>
                  <Card
                    onClick={() => handleDetectionSelection(detection)}
                    style={{
                      border: selectedDetection?._id === detection._id ? "2px solid blue" : "none",
                    }}
                  >
                    <CardContent>
                      <Typography variant="subtitle1">{detection.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {detection.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {selectedDetection && (
          <>
            <Box mb={3}>
              <Typography variant="h6">Step 3: Select Data Point</Typography>
              <FormControl fullWidth>
                <InputLabel>Data Point</InputLabel>
                <Select
                  value={appliedOn}
                  onChange={(e) => setAppliedOn(e.target.value)}
                >
                  {Object.entries(selectedSensor.dataSchema.properties).map(([key, property]) => (
                    <MenuItem key={key} value={key}>
                      {property.description} ({key})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box mb={3}>
              <Typography variant="h6">Step 4: Configure Parameters</Typography>
              <Grid container spacing={2} mt={2}>
                {Object.entries(selectedDetection.params_template).map(([key, param]) => (
                  <Grid item xs={6} key={key}>
                    <TextField
                      label={param.description}
                      type={param.type === "number" ? "number" : "text"}
                      required={param.required}
                      fullWidth
                      value={detectionParams[key] || ""}
                      onChange={(e) => handleParamChange(key, e.target.value)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box mb={3}>
              <Typography variant="h6">Step 5: Set Logic</Typography>
              <FormControl fullWidth>
                <InputLabel>Logic</InputLabel>
                <Select value={logic} onChange={(e) => setLogic(e.target.value)}>
                  <MenuItem value="AND">AND</MenuItem>
                  <MenuItem value="OR">OR</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleAddDetection}
          color="primary"
          variant="contained"
          disabled={!isAddButtonEnabled()}
        >
          Add Detection
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetectionConfigurator;
